import { useCallback } from 'react';
import useSendBiEvent from '../../../hooks/useSendBiEvent';
import useUserInfo from '../../../hooks/useUserInfo';
import { ACTIONS_EVENTS } from '../../../services/events';
import { Template } from '../../../types/UserInfoTypes';
import { MessageResponse } from './InviteUserInput';
import { SweepNotificationVariant } from '../../../reducers/notificationsReducer';
import { useSweepNotifications } from '../../notifications/useSweepNotifications';

export const useInviteUsers = ({
  funnelMapId,
  funnelMapName,
  template,
}: {
  funnelMapId?: string;
  funnelMapName?: string;
  template: Template;
}) => {
  const { sendInvitation, updateHubspotContact, getAccountUsersData } = useUserInfo();
  const sendBiEvent = useSendBiEvent();
  const { addNotification } = useSweepNotifications();

  const sendNewInvitation = useCallback(
    async (email: string, roleGroupId: string, message: string) => {
      sendBiEvent({ name: ACTIONS_EVENTS.inviteUser });
      updateHubspotContact({ lat_invite_date: Date.now() }); //The property was created misspelled in HS
      await sendInvitation({
        email,
        roleGroupId,
        message,
        entryPointId: funnelMapId,
        entryPointName: funnelMapName,
        template,
      });
    },
    [funnelMapId, funnelMapName, sendBiEvent, sendInvitation, template, updateHubspotContact],
  );

  const displayNotification = useCallback(
    (
      messageResponse: MessageResponse,
      variant: SweepNotificationVariant.Success | SweepNotificationVariant.Error,
    ) => {
      addNotification({
        message: messageResponse[variant],
        variant,
      });
    },
    [addNotification],
  );

  const afterSendActions = useCallback(
    async (isError: boolean, messageResponse?: MessageResponse) => {
      messageResponse &&
        displayNotification(
          messageResponse,
          isError ? SweepNotificationVariant.Error : SweepNotificationVariant.Success,
        );
      getAccountUsersData();
    },
    [displayNotification, getAccountUsersData],
  );

  return { sendNewInvitation, afterSendActions, displayNotification };
};
